import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from "@mui/material";
import {useState, useEffect} from "react";
import {RTL} from "../../cache/CacheRtl";
import {getCompanies} from "../../services/system/CompanyService";
import {saveCoupon, updateCoupon} from "../../services/system/CouponService";

const CouponDialog = ({isOpen, close, setReload, editCoupon = null}) => {
    const [formData, setFormData] = useState({
        companyId: "",
        code: "",
        discountPercentage: "",
        validUntil: "",
        maxUsageCount: "",
        duration: ""
    });

    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = editCoupon !== null;

    // Duration options matching the enum
    const durationOptions = [
        { value: "DAY", label: "ליום" },
        { value: "WEEK", label: "לשבוע" },
        { value: "MONTH", label: "לחודש" },
        { value: "YEAR", label: "לשנה" },
        { value: "LIFETIME", label: "לכל החיים" }
    ];

    useEffect(() => {
        if (isOpen) {
            loadCompanies();
            if (isEdit) {
                populateFormForEdit();
            } else {
                resetForm();
            }
        }
    }, [isOpen, editCoupon]);

    const loadCompanies = async () => {
        try {
            const response = await getCompanies();
            setCompanies(response.data || []);
        } catch (error) {
            console.error("Error loading companies:", error);
        }
    };

    const populateFormForEdit = () => {
        setFormData({
            companyId: editCoupon.companyId,
            code: editCoupon.code,
            discountPercentage: editCoupon.discountPercentage,
            validUntil: formatDateForInput(editCoupon.validUntil),
            maxUsageCount: editCoupon.maxUsageCount,
            duration: editCoupon.duration || ""
        });
        // NOTE: selectedCompany is resolved in a dedicated effect below (keyed on `companies`)
        // — companies load asynchronously, so resolving it here would read a stale (empty) list
        // and leave the "חברה *" Autocomplete blank on edit.
    };

    // Resolve the selected-company object once the companies list is available (or when the
    // edited coupon changes). Mirrors CouponSearchBar's companyId→selectedCompany sync.
    useEffect(() => {
        if (isEdit && companies.length > 0) {
            setSelectedCompany(companies.find(c => c.id === editCoupon.companyId) || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies, editCoupon]);

    const resetForm = () => {
        setFormData({
            companyId: "",
            code: "",
            discountPercentage: "",
            validUntil: "",
            maxUsageCount: "",
            duration: ""
        });
        setSelectedCompany(null);
        setErrors({});
    };

    // validUntil is a LocalDate — the server sends a plain yyyy-MM-dd (or a [y,m,d] array), and the
    // date input wants exactly that. Round-tripping through `new Date(...).toISOString()` parsed it
    // as UTC midnight and re-sliced in UTC, which is a no-op only by luck; it breaks outright on the
    // array shape. A calendar day should never be routed through a timezone at all.
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        if (Array.isArray(dateValue)) {
            const [y, m, d] = dateValue;
            return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        }
        return String(dateValue).slice(0, 10);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.companyId) {
            newErrors.companyId = "יש לבחור חברה";
        }

        if (!formData.code.trim()) {
            newErrors.code = "יש להזין קוד קופון";
        } else if (formData.code.length < 3) {
            newErrors.code = "קוד הקופון חייב להכיל לפחות 3 תווים";
        } else if (!/^[A-Z0-9]+$/.test(formData.code.toUpperCase())) {
            newErrors.code = "קוד הקופון יכול להכיל רק אותיות באנגלית ומספרים";
        }

        if (!formData.discountPercentage) {
            newErrors.discountPercentage = "יש להזין אחוז הנחה";
        } else if (formData.discountPercentage < 0 || formData.discountPercentage > 100) {
            newErrors.discountPercentage = "אחוז ההנחה חייב להיות בין 0 ל-100";
        }

        if (!formData.validUntil) {
            newErrors.validUntil = "יש לבחור תאריך תוקף";
        } else {
            const selectedDate = new Date(formData.validUntil);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

            if (selectedDate <= today) {
                newErrors.validUntil = "תאריך התוקף חייב להיות בעתיד";
            }
        }

        if (!formData.maxUsageCount) {
            newErrors.maxUsageCount = "יש להזין מספר שימושים מקסימלי";
        } else if (formData.maxUsageCount < 1) {
            newErrors.maxUsageCount = "מספר השימושים חייב להיות לפחות 1";
        } else if (isEdit && formData.maxUsageCount < editCoupon.currentUsageCount) {
            newErrors.maxUsageCount = `מספר השימושים לא יכול להיות קטן מהשימוש הנוכחי (${editCoupon.currentUsageCount})`;
        }

        // Duration validation (optional field)
        // No validation needed as it's optional

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                ...formData,
                code: formData.code.toUpperCase(),
                discountPercentage: parseInt(formData.discountPercentage),
                maxUsageCount: parseInt(formData.maxUsageCount),
                // Send the picked day verbatim. The server field is a LocalDate (CouponCreateDto /
                // CouponUpdateDto), and formData.validUntil is already yyyy-MM-dd from the date
                // input. Padding to 'T23:59:59' and calling toISOString() converted it to a UTC
                // instant, which a LocalDate cannot parse — and for any user WEST of UTC 23:59
                // local rolls into the NEXT day (verified: America/New_York turns 2026-07-28 into
                // 2026-07-29), silently extending the coupon by a day.
                validUntil: formData.validUntil
            };

            // Only include duration if it's selected
            if (formData.duration) {
                submitData.duration = formData.duration;
            }

            if (isEdit) {
                await updateCoupon(editCoupon.id, submitData);
            } else {
                await saveCoupon(submitData);
            }

            setReload();
            close();
            resetForm();
        } catch (error) {
            console.error("Error saving coupon:", error);
            if (error.response?.data?.message) {
                setErrors({ submit: error.response.data.message });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        close();
        resetForm();
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const getDiscountTypeChip = () => {
        if (formData.discountPercentage === "100" || formData.discountPercentage === 100) {
            return <Chip label="קופון חינם" color="success" size="small" sx={{ml: 1}} />;
        }
        return null;
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            dir="rtl"
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    {isEdit ? "עריכת קופון" : "הוספת קופון חדש"}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>

                    {/* Company Selection */}
                    <RTL>
                        <Autocomplete
                            value={selectedCompany}
                            onChange={(event, newValue) => {
                                setSelectedCompany(newValue);
                                handleInputChange('companyId', newValue?.id || '');
                            }}
                            options={companies}
                            getOptionLabel={(option) => option.name || ''}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="חברה *"
                                    error={!!errors.companyId}
                                    helperText={errors.companyId}
                                    fullWidth
                                />
                            )}
                            disabled={loading}
                        />

                        {/* Coupon Code */}
                        <TextField
                            label="קוד קופון *"
                            value={formData.code}
                            onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                            error={!!errors.code}
                            helperText={errors.code || "אותיות באנגלית ומספרים בלבד"}
                            fullWidth
                            disabled={loading}
                            inputProps={{ style: { textTransform: 'uppercase' } }}
                        />

                        {/* Discount Percentage */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                label="אחוז הנחה *"
                                type="number"
                                value={formData.discountPercentage}
                                onChange={(e) => handleInputChange('discountPercentage', e.target.value)}
                                error={!!errors.discountPercentage}
                                helperText={errors.discountPercentage}
                                fullWidth
                                disabled={loading}
                                inputProps={{ min: 0, max: 100 }}
                            />
                            {getDiscountTypeChip()}
                        </Box>

                        {/* Duration Selection */}
                        <FormControl fullWidth disabled={loading}>
                            <InputLabel>תקופת מנוי</InputLabel>
                            <Select
                                value={formData.duration}
                                onChange={(e) => handleInputChange('duration', e.target.value)}
                                label="תקופת מנוי"
                            >
                                <MenuItem value="">
                                    <em>ללא בחירה</em>
                                </MenuItem>
                                {durationOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Valid Until - DATE ONLY */}
                        <TextField
                            sx={{"& .MuiOutlinedInput-input" : {
                                    textAlign: 'end'}}}
                            label="תוקף עד *"
                            type="date"
                            value={formData.validUntil}
                            onChange={(e) => handleInputChange('validUntil', e.target.value)}
                            error={!!errors.validUntil}
                            helperText={errors.validUntil}
                            fullWidth
                            disabled={loading}
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Max Usage Count */}
                        <TextField
                            label="מספר שימושים מקסימלי *"
                            type="number"
                            value={formData.maxUsageCount}
                            onChange={(e) => handleInputChange('maxUsageCount', e.target.value)}
                            error={!!errors.maxUsageCount}
                            helperText={errors.maxUsageCount || (isEdit ? `שימוש נוכחי: ${editCoupon?.currentUsageCount || 0}` : "")}
                            fullWidth
                            disabled={loading}
                            inputProps={{ min: 1 }}
                        />
                    </RTL>

                    {/* Submit Error */}
                    {errors.submit && (
                        <Typography color="error" variant="body2">
                            {errors.submit}
                        </Typography>
                    )}

                </Box>

            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    variant="contained"
                    sx={{ minWidth: 120 }}
                >
                    {loading ? "שומר..." : (isEdit ? "עדכן" : "צור קופון")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CouponDialog;